/* eslint-disable no-unused-vars */
import clsx from 'clsx'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import {
  BORDER_WEEKLY_CELL,
  EVENT_TYPES,
  MIN_HEIGHT_EVENT,
  MIN_HEIGHT_HIDE_DETAIL,
  MINUTES_PER_HOUR,
  TIME_FORMAT
} from '../../../constants'
import { getIconforEvent } from '../../../icons'
import { IEvent } from '../../../types'
import {
  convertType,
  convertTypeDisable,
  customDateFormat,
  isDisplayIcon
} from '../../../utils'
import styles from '../SAPPCalendar.module.css'
import CustomTooltip from '../common/CustomTooltip'

interface IWeeklyEventProps {
  event: IEvent
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onEventDetail?: (event: IEvent) => void
  zIndex: number
}

const calculateBoxSize = (
  startTime: Date,
  endTime: Date,
  isAllDay?: boolean
) => {
  if (isAllDay) {
    return {
      top: 10,
      height: MIN_HEIGHT_HIDE_DETAIL
    }
  }

  const minutes = dayjs(startTime).minute()
  const diffMinutes = dayjs(endTime).diff(startTime, 'minute', true)

  const border = Math.floor(
    ((minutes + diffMinutes) / MINUTES_PER_HOUR) * BORDER_WEEKLY_CELL
  )

  return {
    top: minutes,
    height: diffMinutes + border
  }
}

const WeeklyEvent = ({
  event,
  onOpenDetail,
  onEventDetail,
  zIndex
}: IWeeklyEventProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [shouldTruncateTitle, setShouldTruncateTitle] = useState(false)
  const [shouldTruncateContent, setShouldTruncateContent] = useState(false)
  const { top, height } = calculateBoxSize(
    event.startDate,
    event.endDate,
    event.isAllDay
  )

  useEffect(() => {
    if (contentRef.current) {
      const actualHeight = contentRef.current.offsetHeight
      const padding = 12 * 2 // 12px top and bottom padding
      const needsTruncation = actualHeight + padding > height

      if (needsTruncation) {
        setShouldTruncateTitle(true)

        if (!shouldTruncateTitle) {
          return
        }

        setShouldTruncateContent(true)
      }
    }
  }, [height, shouldTruncateTitle])

  const isMinHeight = height < MIN_HEIGHT_EVENT

  const formattedDate =
    customDateFormat(event.startDate, TIME_FORMAT) +
    ' - ' +
    customDateFormat(event.endDate, TIME_FORMAT)
  const iconList = getIconforEvent(event, event.isActive)
  const typeClass = event.isActive
    ? convertType[event.type] || 'default'
    : convertTypeDisable[event.type] || 'default'

  const className = event?.isOverDue ? `event-error` : `event-${typeClass}`

  return (
    <div
      className={clsx(styles[className])}
      onClick={() => {
        onOpenDetail && onOpenDetail(dayjs(event.startDate).toDate(), [event])
        onEventDetail && onEventDetail(event)
      }}
      style={{
        top: `${top}px`,
        height: `${Math.max(height, MIN_HEIGHT_EVENT)}px`,
        zIndex
      }}
    >
      <div
        className={clsx(
          styles['weekly-event'],
          isMinHeight && styles['event--min-height']
        )}
      >
        {isDisplayIcon(event) && (
          <div className={styles['weekly-event__title_icon']}>{iconList}</div>
        )}
        <div
          ref={contentRef}
          className={clsx(
            styles['weekly-event__content'],
            height < MIN_HEIGHT_HIDE_DETAIL && styles['weekly-event--collapse']
          )}
        >
          <div
            className={clsx(
              styles['weekly-event__title'],
              shouldTruncateTitle && styles['weekly-event__title--truncate']
            )}
          >
            <CustomTooltip title={event.title}>{event.title}</CustomTooltip>
          </div>
          <div
            className={clsx(
              styles['weekly-event__date'],
              shouldTruncateContent && styles['weekly-event__date--truncate']
            )}
          >
            <CustomTooltip title={formattedDate}>{formattedDate}</CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  )
}
export default WeeklyEvent
